/**
 * Fair times for the rules that run on a clock (10 §1, §2): the train's departure, the column's arrival, the
 * waves at the ford, the strike. None is a hard-coded number of seconds: each comes from the route the
 * validator's own clock gate measures (validate/routes.ts `checkClock`) - start, every target nearest first,
 * pickup, at a walk - taken on the FINISHED map, from `GoalResult.finish`.
 */

import { findPath } from '../../sim/astar';
import { collidersFor } from '../../sim/props';
import { RANGER } from '../../sim/tuning';
import type { MissionDef, ObjectiveRule, PropDef } from '../../sim/types';
import { VALIDATE } from '../tuning';
import { type NavProbe, probeNav, UNREACHABLE } from '../validate/nav';

export type TimedRule = Extract<ObjectiveRule, { type: 'timedEvent' }>;

export interface RouteTimes {
  /** Seconds at a walk from the start to the first target, to the last of them, and on to the pickup. */
  toFirst: number;
  toLast: number;
  total: number;
}

const EXPANSIONS = 200000;

export const round5 = (seconds: number): number => Math.round(seconds / 5) * 5;

/** Walking pace over crawling pace: what a stretch costs the man who crawls all of it. */
export const CAREFUL: number = RANGER.walkSpeed / RANGER.crawlSpeed;

/** The free cell beside a prop that is cheapest to get to (fewest mines and wire), then nearest to `from`. */
function standBeside(nav: NavProbe, prop: PropDef, fromX: number, fromZ: number): number {
  const box = { x0: prop.x, z0: prop.z, x1: prop.x, z1: prop.z };
  for (const c of collidersFor(prop)) {
    const ex = c.shape === 'circle' ? c.r : c.hx;
    const ez = c.shape === 'circle' ? c.r : c.hz;
    box.x0 = Math.min(box.x0, c.x - ex);
    box.x1 = Math.max(box.x1, c.x + ex);
    box.z0 = Math.min(box.z0, c.z - ez);
    box.z1 = Math.max(box.z1, c.z + ez);
  }
  const reach = VALIDATE.vicinity + RANGER.radius;
  let best = -1;
  let bestCost = UNREACHABLE;
  let bestD = Infinity;
  const [zLo, zHi] = [Math.max(0, Math.floor(box.z0 - reach)), Math.min(nav.height - 1, Math.floor(box.z1 + reach))];
  const [xLo, xHi] = [Math.max(0, Math.floor(box.x0 - reach)), Math.min(nav.width - 1, Math.floor(box.x1 + reach))];
  for (let z = zLo; z <= zHi; z++) {
    for (let x = xLo; x <= xHi; x++) {
      const i = z * nav.width + x;
      const cost = nav.hard[i] === 0 ? (nav.cost[i] ?? UNREACHABLE) : UNREACHABLE;
      const d = (x + 0.5 - fromX) * (x + 0.5 - fromX) + (z + 0.5 - fromZ) * (z + 0.5 - fromZ);
      if (cost === UNREACHABLE || cost > bestCost || (cost === bestCost && d >= bestD)) continue;
      best = i;
      bestCost = cost;
      bestD = d;
    }
  }
  return best;
}

/** Metres of the shortest walk between two cells; -1 when there is none. */
function walk(nav: NavProbe, fromX: number, fromZ: number, toCell: number): number {
  if (toCell < 0) return -1;
  const grid = { width: nav.width, height: nav.height, blocked: nav.hard };
  const tx = toCell % nav.width;
  const path = findPath(grid, Math.floor(fromX), Math.floor(fromZ), tx, (toCell - tx) / nav.width, EXPANSIONS);
  if (!path) return -1;
  let metres = 0;
  for (let i = 1; i < path.length; i++) {
    const a = path[i - 1];
    const b = path[i];
    if (a && b) metres += a[0] !== b[0] && a[1] !== b[1] ? Math.SQRT2 : 1;
  }
  return metres;
}

/** The validator's route over the target props; null when some leg cannot be walked (the map will be re-rolled). */
export function measureRoute(def: MissionDef, nav: NavProbe = probeNav(def)): RouteTimes | null {
  const todo = def.props.filter((p) => def.objective.targetPropIds.includes(p.id));
  let x = def.player.x;
  let z = def.player.z;
  let metres = 0;
  const marks: number[] = [];
  while (todo.length > 0) {
    const away = (p: PropDef): number => (p.x - x) * (p.x - x) + (p.z - z) * (p.z - z);
    todo.sort((a, b) => away(a) - away(b));
    const next = todo.shift();
    if (!next) break;
    const cell = standBeside(nav, next, x, z);
    const leg = walk(nav, x, z, cell);
    if (leg < 0) return null;
    metres += leg;
    marks.push(metres);
    x = (cell % nav.width) + 0.5;
    z = Math.floor(cell / nav.width) + 0.5;
  }
  const home = walk(nav, x, z, Math.floor(def.pickup.z) * nav.width + Math.floor(def.pickup.x));
  if (home < 0 || marks.length === 0) return null;
  const pace = RANGER.walkSpeed;
  const last = marks[marks.length - 1] ?? 0;
  return { toFirst: (marks[0] ?? 0) / pace, toLast: last / pace, total: (metres + home) / pace };
}

/**
 * A deadline a careful player can meet: the whole approach at a crawl, then `workSeconds` on the targets -
 * and never less than half the mission clock nor more than four fifths of it, so there is time left to get out.
 */
export function deadlineSeconds(def: MissionDef, route: RouteTimes, workSeconds: number): number {
  const wanted = route.toLast * CAREFUL + workSeconds;
  return round5(Math.min(def.clockSeconds * 0.8, Math.max(def.clockSeconds * 0.5, wanted)));
}

/** Seconds at a run from beside `from` to open ground `clear` metres (and a few more) due south of it; null if none. */
export function escapeSeconds(
  def: MissionDef, from: PropDef, clear: number, nav: NavProbe = probeNav(def),
): number | null {
  const start = standBeside(nav, from, from.x, from.z + 1);
  if (start < 0) return null;
  const goalZ = Math.min(def.sizeZ - 4, from.z + clear + 4);
  for (let ring = 0; ring <= 14; ring++) {
    for (const dx of ring === 0 ? [0] : [-ring, ring]) {
      const gx = Math.floor(from.x) + dx;
      const gz = Math.floor(goalZ);
      if (gx < 1 || gx >= nav.width - 1) continue;
      const cell = gz * nav.width + gx;
      if (nav.hard[cell] !== 0 || nav.soft[cell] !== 0) continue;
      const metres = walk(nav, (start % nav.width) + 0.5, Math.floor(start / nav.width) + 0.5, cell);
      if (metres >= 0) return metres / RANGER.runSpeed;
    }
  }
  return null;
}

/** Set the time of the first timed rule that matches (the rules object is the finished mission's own). */
export function retime(def: MissionDef, match: (rule: TimedRule) => boolean, seconds: number): void {
  const rule = def.objective.rules.find((r): r is TimedRule => r.type === 'timedEvent' && match(r));
  if (rule) rule.atSeconds = seconds;
}
